import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { i18n, type Locale } from '@/i18n';

const STORAGE_KEY = 'optimalhr.locale';

/**
 * Locale switcher. Persists the user's choice to localStorage so it
 * survives reloads. Used by the user-menu's "Switch language" item.
 */
export function useLocale() {
  const { locale, t } = useI18n();
  const current = computed<Locale>(() => locale.value as Locale);

  function setLocale(next: Locale) {
    locale.value = next;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore — sessionStorage in private mode etc. */
    }
  }

  function toggle() {
    setLocale(current.value === 'en' ? 'km' : 'en');
  }

  return { locale: current, setLocale, toggle, t };
}

/**
 * Read the persisted locale on app boot. Called once from i18n setup.
 */
export function readPersistedLocale(): Locale | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'en' || v === 'km') return v;
  } catch {
    /* ignore */
  }
  return null;
}

// Re-export so callers don't need to import the i18n instance themselves.
export { i18n };
